// Presentation helpers shared across views.
//
// All functions are locale-aware: dates and numbers go through Intl.* with the
// active i18next locale, and the relative/duration strings live in the
// `common` catalog (`time.*`, `duration.*`, `countdown.*`) so they translate.
// i18next is initialised at module load (see i18n.js), so `i18n.t` is safe to
// call here even outside React render.

import { i18n, getActiveLocale } from './i18n.js';

const locale = () => getActiveLocale();

/** "just now", "12m ago", "3h ago", "yesterday", "2 days ago", or an absolute date. */
export function relativeTime(value) {
  const then = new Date(value).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 45) return i18n.t('time.justNow');
  if (seconds < 90) return i18n.t('time.minutesAgo', { count: 1 });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return i18n.t('time.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return i18n.t('time.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  if (days < 30) return i18n.t('time.daysAgo', { count: days });
  return absoluteDate(value);
}

/** "14 March" — or with the year, when it isn't this year. */
export function absoluteDate(value) {
  const date = new Date(value);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(locale(), {
    day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function absoluteDateTime(value) {
  return new Date(value).toLocaleString(locale(), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** "expires in 2 days" / "expires in 4h" — a countdown, not a timestamp. */
export function timeUntil(value) {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  if (seconds <= 0) return i18n.t('time.expired');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return i18n.t('duration.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return i18n.t('duration.hours', { count: hours });
  return i18n.t('duration.days', { count: Math.round(hours / 24) });
}

/** Countdown for the corroboration cooldown: "43m", "2m", "20s". */
export function countdown(target) {
  const seconds = Math.max(0, Math.round((new Date(target).getTime() - Date.now()) / 1000));
  if (seconds >= 60) return i18n.t('countdown.minutes', { count: Math.ceil(seconds / 60) });
  return i18n.t('countdown.seconds', { count: seconds });
}

export function metres(value) {
  if (value == null) return '';
  const loc = locale();
  if (value < 1000) return `${new Intl.NumberFormat(loc).format(Math.round(value))} m`;
  return `${new Intl.NumberFormat(loc, { maximumFractionDigits: 1 }).format(value / 1000)} km`;
}
